import React, { useState, useEffect } from 'react';
import { useParams, Link, useSearchParams } from 'react-router-dom';
import { CheckCircle, XCircle, Clock, ArrowLeft, Loader2, MessageCircle, Home, FileText } from 'lucide-react';
import { trackWaConversion } from '../utils/trackConversion';
import { trackPurchase } from '../utils/ga4';

const API_URL = process.env.REACT_APP_BACKEND_URL;

const PaymentResultPage = () => {
  const { slug, status } = useParams();
  const [searchParams] = useSearchParams();
  const [order, setOrder] = useState(null);
  const [loading, setLoading] = useState(true);
  const [purchaseTracked, setPurchaseTracked] = useState(false);

  useEffect(() => {
    const fetchOrder = async () => {
      try {
        const paymentId = searchParams.get('payment_id');
        const extRef = searchParams.get('external_reference');
        const params = new URLSearchParams();
        if (extRef) params.set('external_reference', extRef);
        if (paymentId) params.set('payment_id', paymentId);

        if (params.toString()) {
          const res = await fetch(`${API_URL}/api/payments/status?${params.toString()}`);
          if (res.ok) setOrder(await res.json());
        }
      } catch (e) {
        console.error('Error fetching order:', e);
      } finally {
        setLoading(false);
      }
    };
    fetchOrder();
  }, [searchParams]);

  // GA4: purchase — dispara uma única vez quando status = sucesso e order carregado
  useEffect(() => {
    if (!purchaseTracked && order && status === 'sucesso') {
      trackPurchase({
        orderId: order.id,
        items: order.items || [],
        total: order.total || 0,
        partner: slug,
      });
      setPurchaseTracked(true);
    }
  }, [order, status, slug, purchaseTracked]);

  const configs = {
    sucesso: {
      icon: CheckCircle,
      iconColor: 'text-green-500',
      bgColor: 'bg-green-50',
      borderColor: 'border-green-200',
      title: 'Pagamento Aprovado!',
      subtitle: 'Seu pedido foi confirmado com sucesso.',
      description: 'Você receberá um email com os detalhes do seu pedido. O vendedor entrará em contato para combinar a entrega.',
    },
    falha: {
      icon: XCircle,
      iconColor: 'text-red-500',
      bgColor: 'bg-red-50',
      borderColor: 'border-red-200',
      title: 'Pagamento não aprovado',
      subtitle: 'Houve um problema com seu pagamento.',
      description: 'Tente novamente com outro método de pagamento ou entre em contato com o vendedor via WhatsApp.',
    },
    pendente: {
      icon: Clock,
      iconColor: 'text-amber-500',
      bgColor: 'bg-amber-50',
      borderColor: 'border-amber-200',
      title: 'Pagamento Pendente',
      subtitle: 'Estamos aguardando a confirmação do seu pagamento.',
      description: 'Pagamentos via PIX são confirmados em segundos. Boletos podem levar até 3 dias úteis para compensar.',
    },
  };

  const config = configs[status] || configs.pendente;
  const Icon = config.icon;

  if (loading) {
    return (
      <div className="min-h-screen bg-gray-50 flex items-center justify-center">
        <Loader2 className="w-10 h-10 animate-spin text-[#162289]" />
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gray-50" data-testid="payment-result-page">
      {/* Header */}
      <div className="bg-[#0a1555] py-3 px-6">
        <div className="max-w-[800px] mx-auto flex items-center justify-between">
          <Link to={`/parceiros/${slug}`} className="flex items-center gap-2 text-white/80 hover:text-white text-sm" data-testid="back-to-store">
            <ArrowLeft className="w-4 h-4" />
            Voltar para a loja
          </Link>
        </div>
      </div>

      <div className="max-w-[600px] mx-auto px-4 py-12">
        <div className={`${config.bgColor} ${config.borderColor} border rounded-2xl p-8 text-center`} data-testid="payment-status-card">
          <Icon className={`w-16 h-16 mx-auto ${config.iconColor} mb-4`} />
          <h1 className="text-2xl font-bold text-gray-900 mb-2" data-testid="payment-title">
            {config.title}
          </h1>
          <p className="text-gray-600 mb-2">{config.subtitle}</p>
          <p className="text-sm text-gray-500">{config.description}</p>
        </div>

        {/* Order Details */}
        {order && (
          <div className="mt-6 bg-white rounded-2xl p-6 shadow-sm" data-testid="order-details">
            <h2 className="font-semibold text-gray-900 mb-4 flex items-center gap-2">
              <FileText className="w-5 h-5 text-[#162289]" />
              Detalhes do Pedido
            </h2>
            <div className="space-y-2 text-sm">
              {order.id && (
                <div className="flex justify-between py-2 border-b border-gray-50">
                  <span className="text-gray-500">Pedido</span>
                  <span className="font-mono text-gray-700 text-xs">{order.id.slice(0, 8).toUpperCase()}</span>
                </div>
              )}
              {order.items?.map((item, i) => (
                <div key={i} className="flex justify-between py-2 border-b border-gray-50">
                  <span className="text-gray-600">{item.name} x{item.quantity}</span>
                  <span className="text-gray-900 font-medium">R$ {(item.unit_price * item.quantity).toFixed(2)}</span>
                </div>
              ))}
              {order.shipping_cost > 0 && (
                <div className="flex justify-between py-2 border-b border-gray-50">
                  <span className="text-gray-500">Frete</span>
                  <span className="text-gray-900">R$ {order.shipping_cost.toFixed(2)}</span>
                </div>
              )}
              {order.total && (
                <div className="flex justify-between py-2 font-bold text-gray-900">
                  <span>Total</span>
                  <span>R$ {order.total.toFixed(2)}</span>
                </div>
              )}
              {order.payment?.payment_type && (
                <div className="flex justify-between py-2 text-gray-500">
                  <span>Método</span>
                  <span className="capitalize">{order.payment.payment_type === 'credit_card' ? 'Cartão de crédito' : order.payment.payment_type === 'bank_transfer' ? 'PIX' : order.payment.payment_type === 'ticket' ? 'Boleto' : order.payment.payment_type}</span>
                </div>
              )}
            </div>
          </div>
        )}

        {/* Actions */}
        <div className="mt-6 space-y-3">
          <Link
            to={`/parceiros/${slug}`}
            className="w-full flex items-center justify-center gap-2 py-3.5 bg-[#162289] hover:bg-[#1a2fa0] text-white rounded-xl font-semibold transition-colors"
            data-testid="back-to-store-btn"
          >
            <Home className="w-5 h-5" />
            Voltar para a loja
          </Link>
          <a
            href={`https://wa.me/556333224310?text=${encodeURIComponent('🟢 Olá! Vim do site, tenho interesse em comprar peças.')}`}
            target="_blank"
            rel="noopener noreferrer"
            className="w-full flex items-center justify-center gap-2 py-3.5 bg-green-500 hover:bg-green-600 text-white rounded-xl font-semibold transition-colors"
            data-testid="whatsapp-btn"
          >
            <MessageCircle className="w-5 h-5" />
            Falar com o vendedor
          </a>
        </div>
      </div>
    </div>
  );
};

export default PaymentResultPage;
